import {
  ChartBarIcon,
  UsersIcon,
  CurrencyPoundIcon,
  ArrowPathIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import L from 'leaflet';
import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import 'leaflet/dist/leaflet.css';
import 'leaflet.heat';

import {
  statistics,
  monthlyRevenue,
  membershipTypeDistribution,
  members,
  payments,
  addresses,
} from '../../utils/stubData';

// HeatmapLayer component to integrate leaflet.heat with React Leaflet
function HeatmapLayer({ points }) {
  const map = useMap();

  useEffect(() => {
    if (!points || points.length === 0) return;

    // Create heatmap layer with custom gradient
    const heatLayer = L.heatLayer(points, {
      radius: 35,
      blur: 25,
      maxZoom: 10,
      max: 1.0,
      gradient: {
        0.0: '#3498db',
        0.3: '#2ecc71',
        0.5: '#f1c40f',
        0.7: '#e67e22',
        1.0: '#e74c3c',
      },
    }).addTo(map);

    return () => {
      map.removeLayer(heatLayer);
    };
  }, [map, points]);

  return null;
}

function Reports() {
  const [selectedReport, setSelectedReport] = useState('overview');
  const [dateRange, setDateRange] = useState('thisYear');

  const reportTypes = [
    { id: 'overview', name: 'Overview Summary', icon: ChartBarIcon },
    { id: 'membership', name: 'Membership Report', icon: UsersIcon },
    { id: 'financial', name: 'Financial Report', icon: CurrencyPoundIcon },
    { id: 'renewal', name: 'Renewal Status', icon: ArrowPathIcon },
    { id: 'demographics', name: 'Demographics', icon: MapPinIcon },
  ];

  const OverviewReport = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Total Members</h4>
          <p className="text-3xl font-bold mt-2 text-hdlcc-textDark">{statistics.totalMembers}</p>
          <p className="text-sm text-hdlcc-textMuted mt-1">All-time</p>
        </div>
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Active Members</h4>
          <p className="text-3xl font-bold mt-2 text-hdlcc-accentGreen">
            {statistics.activeMembers}
          </p>
          <p className="text-sm text-hdlcc-textMuted mt-1">Currently active</p>
        </div>
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Revenue (Year)</h4>
          <p className="text-3xl font-bold mt-2 text-hdlcc-textDark">
            £{statistics.revenueThisYear.toLocaleString()}
          </p>
          <p className="text-sm text-hdlcc-textMuted mt-1">2025 total</p>
        </div>
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Growth Rate</h4>
          <p className="text-3xl font-bold mt-2 text-hdlcc-accentGreen">+12.5%</p>
          <p className="text-sm text-hdlcc-textMuted mt-1">Year over year</p>
        </div>
      </div>

      <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
        <h3 className="text-lg font-bold mb-4 text-hdlcc-textDark">Monthly Revenue Trend</h3>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={monthlyRevenue} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="month" tick={{ fill: '#95a5a6', fontSize: 12 }} stroke="#95a5a6" />
            <YAxis tick={{ fill: '#95a5a6', fontSize: 12 }} stroke="#95a5a6" />
            <Tooltip
              contentStyle={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '4px',
              }}
              formatter={value => [`£${value.toLocaleString()}`, 'Revenue']}
            />
            <Legend />
            <Bar dataKey="revenue" fill="#e74c3c" name="Revenue (£)" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );

  const MembershipReport = () => {
    const COLORS = ['#3498db', '#2ecc71', '#f1c40f', '#9b59b6', '#e74c3c'];

    return (
      <div className="space-y-6">
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h3 className="text-lg font-bold mb-4 text-hdlcc-textDark">
            Membership Type Distribution
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={membershipTypeDistribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={entry => `${entry.type}: ${entry.percentage.toFixed(1)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="count"
              >
                {membershipTypeDistribution.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                }}
                formatter={(value, name, props) => [
                  `${value} members (${props.payload.percentage.toFixed(1)}%)`,
                  props.payload.type,
                ]}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
            <h4 className="text-lg font-bold mb-4 text-hdlcc-textDark">New Members by Month</h4>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={monthlyRevenue.slice(-6)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="month" tick={{ fill: '#95a5a6', fontSize: 12 }} stroke="#95a5a6" />
                <YAxis tick={{ fill: '#95a5a6', fontSize: 12 }} stroke="#95a5a6" />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                  }}
                  formatter={value => [`${value} members`, 'New Members']}
                />
                <Bar dataKey="members" fill="#2ecc71" name="New Members" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
            <h4 className="text-lg font-bold mb-4 text-hdlcc-textDark">Status Breakdown</h4>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-hdlcc-textDark">Active</span>
                <span className="font-semibold text-hdlcc-accentGreen">
                  {statistics.activeMembers}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-hdlcc-textDark">Pending</span>
                <span className="font-semibold text-yellow-400">{statistics.pendingApprovals}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-hdlcc-textDark">Expired</span>
                <span className="font-semibold text-hdlcc-accentRed">
                  {statistics.expiredMembers}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const FinancialReport = () => {
    const completedPayments = payments.filter(p => p.status === 'completed');
    const totalRevenue = completedPayments.reduce((sum, p) => sum + p.amount, 0);
    const averageTransaction = totalRevenue / completedPayments.length;

    const paymentMethodData = [
      {
        method: 'Stripe (Card)',
        count: payments.filter(p => p.paymentMethod === 'stripe').length,
      },
      {
        method: 'Bank Transfer',
        count: payments.filter(p => p.paymentMethod === 'bank_transfer').length,
      },
      { method: 'Cash', count: payments.filter(p => p.paymentMethod === 'cash').length },
      {
        method: 'Cheque',
        count: payments.filter(p => p.paymentMethod === 'cheque').length,
      },
    ];

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
            <h4 className="text-hdlcc-textMuted text-sm">Total Revenue</h4>
            <p className="text-3xl font-bold mt-2 text-hdlcc-textDark">
              £{totalRevenue.toFixed(2)}
            </p>
            <p className="text-sm text-hdlcc-textMuted mt-1">
              {completedPayments.length} transactions
            </p>
          </div>
          <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
            <h4 className="text-hdlcc-textMuted text-sm">Average Transaction</h4>
            <p className="text-3xl font-bold mt-2 text-hdlcc-textDark">
              £{averageTransaction.toFixed(2)}
            </p>
            <p className="text-sm text-hdlcc-textMuted mt-1">Per payment</p>
          </div>
          <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
            <h4 className="text-hdlcc-textMuted text-sm">Outstanding</h4>
            <p className="text-3xl font-bold mt-2 text-yellow-400">
              £
              {payments
                .filter(p => p.status === 'pending')
                .reduce((sum, p) => sum + p.amount, 0)
                .toFixed(2)}
            </p>
            <p className="text-sm text-hdlcc-textMuted mt-1">Pending payments</p>
          </div>
        </div>

        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h3 className="text-lg font-bold mb-4 text-hdlcc-textDark">Payment Methods</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={paymentMethodData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" tick={{ fill: '#95a5a6', fontSize: 12 }} stroke="#95a5a6" />
              <YAxis
                type="category"
                dataKey="method"
                tick={{ fill: '#95a5a6', fontSize: 12 }}
                stroke="#95a5a6"
                width={120}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '4px',
                }}
                formatter={value => [`${value} payments`, 'Count']}
              />
              <Bar dataKey="count" fill="#e74c3c" name="Payments" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const DemographicsReport = () => {
    // Approximate geocoding for UK cities based on stub data
    const cityCoordinates = {
      Reading: [51.4543, -0.9781],
      Manchester: [53.4808, -2.2426],
      Bristol: [51.4545, -2.5879],
      Birmingham: [52.4862, -1.8904],
      Leeds: [53.8008, -1.5491],
      Liverpool: [53.4084, -2.9916],
      Newcastle: [54.9783, -1.6178],
      Sheffield: [53.3811, -1.4701],
      Nottingham: [52.9548, -1.1581],
      Leicester: [52.6369, -1.1398],
    };

    // Count members by location
    const locationData = members.reduce((acc, member) => {
      const address = addresses.find(a => a.id === member.addressId);
      if (address) {
        const key = `${address.town}, ${address.county}`;
        acc[key] = (acc[key] || 0) + 1;
      }
      return acc;
    }, {});

    // Create heatmap points - each member gets a point at their location
    // Format: [latitude, longitude, intensity]
    const heatmapPoints = members.reduce((points, member) => {
      const address = addresses.find(a => a.id === member.addressId);
      if (address && cityCoordinates[address.town]) {
        const coords = cityCoordinates[address.town];
        // Add point for each member at this location
        points.push([coords[0], coords[1], 1]);
      }
      return points;
    }, []);

    const topLocations = Object.entries(locationData)
      .map(([location, count]) => ({ location, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);

    // Count by county
    const countyData = members.reduce((acc, member) => {
      const address = addresses.find(a => a.id === member.addressId);
      if (address && address.county) {
        acc[address.county] = (acc[address.county] || 0) + 1;
      }
      return acc;
    }, {});

    const topCounties = Object.entries(countyData)
      .map(([county, count]) => ({ county, count }))
      .sort((a, b) => b.count - a.count);

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
            <h4 className="text-hdlcc-textMuted text-sm">Total Locations</h4>
            <p className="text-3xl font-bold mt-2 text-hdlcc-textDark">
              {Object.keys(locationData).length}
            </p>
            <p className="text-sm text-hdlcc-textMuted mt-1">Unique cities/towns</p>
          </div>
          <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
            <h4 className="text-hdlcc-textMuted text-sm">Counties Covered</h4>
            <p className="text-3xl font-bold mt-2 text-hdlcc-accentGreen">
              {Object.keys(countyData).length}
            </p>
            <p className="text-sm text-hdlcc-textMuted mt-1">Geographic spread</p>
          </div>
          <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
            <h4 className="text-hdlcc-textMuted text-sm">Top Location</h4>
            <p className="text-3xl font-bold mt-2 text-hdlcc-textDark">{topLocations[0]?.count}</p>
            <p className="text-sm text-hdlcc-textMuted mt-1">{topLocations[0]?.location}</p>
          </div>
        </div>

        {/* UK Map with Member Heatmap */}
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h3 className="text-lg font-bold mb-4 text-hdlcc-textDark">
            Member Distribution Heatmap
          </h3>
          <div style={{ height: '500px', width: '100%' }}>
            <MapContainer
              center={[54.0, -2.5]}
              zoom={6}
              style={{ height: '100%', width: '100%', borderRadius: '8px' }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <HeatmapLayer points={heatmapPoints} />
            </MapContainer>
          </div>
          <p className="text-xs text-hdlcc-textMuted mt-2">
            Heat intensity shows member concentration across UK locations (blue = low, red = high)
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
            <h3 className="text-lg font-bold mb-4 text-hdlcc-textDark">Top 10 Locations</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topLocations} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fill: '#95a5a6', fontSize: 12 }} stroke="#95a5a6" />
                <YAxis
                  type="category"
                  dataKey="location"
                  tick={{ fill: '#95a5a6', fontSize: 11 }}
                  stroke="#95a5a6"
                  width={150}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                  }}
                  formatter={value => [`${value} members`, 'Count']}
                />
                <Bar dataKey="count" fill="#e74c3c" name="Members" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
            <h3 className="text-lg font-bold mb-4 text-hdlcc-textDark">Distribution by County</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={topCounties}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={entry => `${entry.county}: ${entry.count}`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="count"
                >
                  {topCounties.map((entry, index) => {
                    const colors = [
                      '#3498db',
                      '#2ecc71',
                      '#e74c3c',
                      '#f1c40f',
                      '#9b59b6',
                      '#1abc9c',
                      '#e67e22',
                    ];
                    return <Cell key={`cell-${index}`} fill={colors[index % colors.length]} />;
                  })}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#ffffff',
                    border: '1px solid #e5e7eb',
                    borderRadius: '4px',
                  }}
                  formatter={(value, name, props) => [
                    `${value} members (${((value / members.length) * 100).toFixed(1)}%)`,
                    props.payload.county,
                  ]}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h3 className="text-lg font-bold mb-4 text-hdlcc-textDark">Geographic Distribution</h3>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-4 py-2 text-left text-sm text-hdlcc-textDark">Location</th>
                  <th className="px-4 py-2 text-left text-sm text-hdlcc-textDark">County</th>
                  <th className="px-4 py-2 text-left text-sm text-hdlcc-textDark">Members</th>
                  <th className="px-4 py-2 text-left text-sm text-hdlcc-textDark">Percentage</th>
                </tr>
              </thead>
              <tbody>
                {topLocations.map((item, index) => {
                  const [town, county] = item.location.split(', ');
                  return (
                    <tr key={index} className="border-b border-gray-200/50">
                      <td className="px-4 py-2 text-sm text-hdlcc-textDark">{town}</td>
                      <td className="px-4 py-2 text-sm text-hdlcc-textMuted">{county}</td>
                      <td className="px-4 py-2 text-sm text-hdlcc-textDark font-semibold">
                        {item.count}
                      </td>
                      <td className="px-4 py-2 text-sm text-hdlcc-textDark">
                        {((item.count / members.length) * 100).toFixed(1)}%
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const RenewalReport = () => (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Expiring (30 Days)</h4>
          <p className="text-3xl font-bold mt-2 text-yellow-400">
            {statistics.expiringWithin30Days}
          </p>
          <p className="text-sm text-hdlcc-textMuted mt-1">Requires attention</p>
        </div>
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Expiring (60 Days)</h4>
          <p className="text-3xl font-bold mt-2 text-hdlcc-textDark">
            {statistics.expiringWithin60Days}
          </p>
          <p className="text-sm text-hdlcc-textMuted mt-1">Upcoming renewals</p>
        </div>
        <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
          <h4 className="text-hdlcc-textMuted text-sm">Expiring (90 Days)</h4>
          <p className="text-3xl font-bold mt-2 text-hdlcc-textDark">
            {statistics.expiringWithin90Days}
          </p>
          <p className="text-sm text-hdlcc-textMuted mt-1">Plan ahead</p>
        </div>
      </div>

      <div className="bg-hdlcc-bgWhite p-4 rounded shadow-card border border-gray-200">
        <h3 className="text-lg font-bold mb-4 text-hdlcc-textDark">Renewal Timeline</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-2 text-left text-sm text-hdlcc-textDark">Member</th>
                <th className="px-4 py-2 text-left text-sm text-hdlcc-textDark">Type</th>
                <th className="px-4 py-2 text-left text-sm text-hdlcc-textDark">Renewal Date</th>
                <th className="px-4 py-2 text-left text-sm text-hdlcc-textDark">Days Until</th>
              </tr>
            </thead>
            <tbody>
              {members
                .filter(m => m.status === 'active')
                .sort((a, b) => new Date(a.renewalDate) - new Date(b.renewalDate))
                .slice(0, 10)
                .map(member => {
                  const daysUntil = Math.ceil(
                    (new Date(member.renewalDate) - new Date()) / (1000 * 60 * 60 * 24)
                  );
                  return (
                    <tr key={member.membershipNumber} className="border-b border-gray-200/50">
                      <td className="px-4 py-2 text-sm text-hdlcc-textDark">
                        {member.firstName} {member.lastName}
                      </td>
                      <td className="px-4 py-2 text-sm capitalize text-hdlcc-textDark">
                        {member.membershipNumber}
                      </td>
                      <td className="px-4 py-2 text-sm text-hdlcc-textDark">
                        {new Date(member.renewalDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <span
                          className={`${
                            daysUntil < 30
                              ? 'text-hdlcc-accentRed'
                              : daysUntil < 60
                                ? 'text-yellow-400'
                                : 'text-hdlcc-accentGreen'
                          }`}
                        >
                          {daysUntil} days
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderReport = () => {
    switch (selectedReport) {
      case 'overview':
        return <OverviewReport />;
      case 'membership':
        return <MembershipReport />;
      case 'financial':
        return <FinancialReport />;
      case 'renewal':
        return <RenewalReport />;
      case 'demographics':
        return <DemographicsReport />;
      default:
        return null;
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-hdlcc-textDark uppercase">Reports</h2>
          <p className="text-hdlcc-textMuted">Analyze membership and financial data</p>
        </div>
        <div className="flex space-x-2">
          <select
            value={dateRange}
            onChange={e => setDateRange(e.target.value)}
            className="p-2 border rounded bg-white text-hdlcc-textDark border-gray-300 focus:border-hdlcc-accentRed focus:outline-none"
          >
            <option value="thisMonth">This Month</option>
            <option value="lastMonth">Last Month</option>
            <option value="thisYear">This Year</option>
            <option value="lastYear">Last Year</option>
            <option value="allTime">All Time</option>
          </select>
          <button className="bg-hdlcc-accentRed text-white px-4 py-2 rounded hover:bg-red-600 transition-colors">
            Export Report
          </button>
        </div>
      </div>

      {/* Report Type Selector */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
        {reportTypes.map(report => {
          const IconComponent = report.icon;
          return (
            <button
              key={report.id}
              onClick={() => setSelectedReport(report.id)}
              className={`p-4 rounded shadow-card transition-all ${
                selectedReport === report.id
                  ? 'bg-hdlcc-accentRed text-white'
                  : 'bg-hdlcc-bgWhite text-hdlcc-textDark hover:bg-gray-50 hover:border-hdlcc-accentRed border border-gray-200'
              }`}
            >
              <IconComponent className="w-8 h-8 mx-auto mb-2" />
              <div className="text-sm font-semibold">{report.name}</div>
            </button>
          );
        })}
      </div>

      {/* Report Content */}
      {renderReport()}
    </div>
  );
}

export default Reports;
